/**
 * FontResolverWrapper - Utility for resolving font UIDs with error handling
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides a safe wrapper around font resolution that handles
 * errors gracefully and falls back to using the font family name as the UID.
 */

import { fontResolver } from '../../../../wordprocessing/services/FontResolver';

/**
 * Resolve font UID with error handling
 * 
 * Attempts to resolve a font UID using the FontResolver service.
 * If resolution fails, falls back to using the font family name as the UID.
 * 
 * @param fontFamily - Font family name
 * @param fontWeight - Font weight (default: 400)
 * @param fontStyle - Font style (default: 'normal')
 * @returns Resolved font UID (or fontFamily if resolution fails)
 */
export function resolveFontUidSafe(
  fontFamily: string,
  fontWeight: number = 400,
  fontStyle: 'normal' | 'italic' | 'oblique' = 'normal'
): string {
  try {
    return fontResolver.resolveFontUidSync(fontFamily, fontWeight, fontStyle);
  } catch (e) {
    console.warn(`[FontResolverWrapper] FontResolver failed for ${fontFamily}/${fontWeight}/${fontStyle}; using family name as uid`);
    return fontFamily;
  }
}

/**
 * Resolve font UID with custom fallback
 * 
 * Attempts to resolve a font UID using the FontResolver service.
 * If resolution fails, returns the provided fallback value.
 * 
 * @param fontFamily - Font family name
 * @param fontWeight - Font weight
 * @param fontStyle - Font style
 * @param fallback - Fallback UID to use if resolution fails
 * @returns Resolved font UID or fallback value
 */
export function resolveFontUidWithFallback(
  fontFamily: string,
  fontWeight: number,
  fontStyle: 'normal' | 'italic' | 'oblique',
  fallback: string
): string {
  try {
    return fontResolver.resolveFontUidSync(fontFamily, fontWeight, fontStyle);
  } catch (e) {
    console.warn(`[FontResolverWrapper] FontResolver failed for ${fontFamily}/${fontWeight}/${fontStyle}; using fallback: ${fallback}`);
    return fallback;
  }
}


